import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Loader2, Save, Share2, Check } from "lucide-react";

/**
 * Admin panel for editing public social-media URLs (FB, IG, YT, Google
 * Reviews, TikTok). Empty value = hidden everywhere on the site.
 */
const FIELDS = [
  { key: "facebook", label: "Facebook", placeholder: "https://facebook.com/apex-detailing" },
  { key: "instagram", label: "Instagram", placeholder: "https://instagram.com/apex.detailing" },
  { key: "youtube", label: "YouTube", placeholder: "https://youtube.com/@apexdetailing" },
  { key: "tiktok", label: "TikTok", placeholder: "https://tiktok.com/@apexdetailing" },
  { key: "googleReviews", label: "Google recenze", placeholder: "https://g.page/r/CXXXX/review" },
];

export default function SocialLinksPanel() {
  const [links, setLinks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = () => {
    setLoading(true);
    api
      .get("/social-links/public")
      .then((r) => setLinks(r.data?.links || {}))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const setField = (key, value) => setLinks((l) => ({ ...l, [key]: value }));

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const { data } = await api.put("/social-links", { links });
      setLinks(data?.links || links);
      setMsg({ kind: "ok", text: "Odkazy uloženy." });
    } catch (e) {
      setMsg({ kind: "err", text: e?.response?.data?.detail || "Uložení selhalo." });
    } finally {
      setSaving(false);
    }
  };

  if (loading || !links) return <Loader2 className="w-6 h-6 animate-spin text-apex-gold" />;

  return (
    <div data-testid="social-links-panel">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="font-display text-2xl text-apex-cream font-bold flex items-center gap-2">
            <Share2 className="w-5 h-5 text-apex-gold" />
            Sociální sítě
          </h3>
          <p className="text-apex-cream/60 text-sm mt-1">
            Vyplňte odkazy na vaše profily. Prázdné pole znamená, že se daná ikona/tlačítko skryje.
          </p>
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="btn-gold !py-2 !px-4 text-sm disabled:opacity-50"
          data-testid="social-links-save-btn"
        >
          <Save className="w-4 h-4" /> {saving ? "Ukládá…" : "Uložit"}
        </button>
      </div>

      {msg && (
        <div
          className={`mb-4 rounded-lg px-4 py-2 text-sm ${
            msg.kind === "ok" ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/30" : "bg-red-500/10 text-red-300 border border-red-500/30"
          }`}
          data-testid="social-links-msg"
        >
          {msg.kind === "ok" && <Check className="w-3.5 h-3.5 inline mr-1.5" />}
          {msg.text}
        </div>
      )}

      <div className="rounded-2xl bg-apex-card border border-apex-line p-5 space-y-4">
        {FIELDS.map((f) => (
          <label key={f.key} className="block">
            <div className="text-xs text-apex-cream/70 mb-1">{f.label}</div>
            <input
              type="url"
              value={links[f.key] ?? ""}
              placeholder={f.placeholder}
              onChange={(e) => setField(f.key, e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-apex-bg border border-apex-line text-apex-cream text-sm focus:outline-none focus:border-apex-gold/60"
              data-testid={`social-${f.key}-input`}
            />
          </label>
        ))}
      </div>
    </div>
  );
}
